/**
 * Makes every route paint in the frame it is clicked in.
 *
 * What was slow was never the code or the bytes: all five pages are in one
 * bundle, and the service worker precaches every hashed file (ticket 006). What
 * cost the half second was that a freshly mounted <img> is not `complete`, so
 * `.bg-wrapper` started at `opacity: 0` and only revealed itself from `onLoad` —
 * a frame or more later — and then faded in over 500 ms, on every visit.
 *
 * So this module does two things: it decodes each route's artwork while the
 * landing page is idle, and it remembers what it decoded, so
 * `useBackgroundWrapper` can render the finished state on the very first pass
 * instead of animating up to it. See tasks-completed/007-instant-route-paint.md.
 *
 * Home was the one route left out, because its background was too big to hold;
 * `bun run instant` measured the way back at 527 ms / 16 frames against 12 ms /
 * 1 frame now that it is warmed. See tasks-completed/010-oversized-home-background.md.
 */

import {
  cityOfThirst,
  contactBackground,
  live,
  liveBgSky,
  menuContact1Crop,
  nyBakgrund,
  paradiseStill,
  signupPoster,
  skyBakgrund,
  watchBakgrundNy,
  watchLogga,
} from "./assets";
import {
  afterLoadThenIdle,
  type Connection,
  type IdleDeps,
  navigatorConnection,
  shouldPreload,
} from "./idle";
import { CITY_OF_THIRST_RELEASED } from "./release";

/**
 * What each route needs in order to look finished, background first. These are
 * the bundler's hashed URLs, so they cannot drift from what actually ships.
 *
 * Every entry here is a decoded bitmap held for the life of the tab, so the list
 * is a memory budget as much as a warm list: the ten come to roughly 32 MB
 * decoded, which is a fair price for removing a half-second fade from every
 * navigation on the site.
 *
 * "/" was left out when this was written, and is here now because the reason it
 * was left out is gone. Home's background was 3911 × 4050 — 60 MB decoded, three
 * times everything else put together — for a layout no wider than 920 px. The
 * optimizer now caps what it ships (see `MAX_WIDTH` in
 * scripts/image-optimizer.ts) and the same artwork is 1840 × 1904, or 13 MB:
 * still the largest single entry, but the same order as the rest of the list
 * rather than dwarfing it.
 *
 * It is also the cheapest entry to warm, which is easy to miss. The idle sweep
 * runs while the visitor is on Home, so that bitmap is already decoded and
 * resident — warming it schedules no decode anyone waits for. What the entry
 * actually buys is holding it *after* they navigate away, so the wordmark back
 * to Home paints in the frame it is clicked in, like the way out already does.
 * See tasks-completed/010-oversized-home-background.md.
 *
 * Home is last so the three routes a visitor can click *from* Home are warm
 * first; the return trip is by definition later than the outbound one.
 *
 * Built by a function rather than written as a literal so both sides of the
 * release flag stay testable. Read straight from the const, only whichever list
 * the module happened to be first evaluated with can ever be asserted, and
 * `mock.module` cannot get behind it.
 */
export function buildRouteAssets(released: boolean): Readonly<Record<string, readonly string[]>> {
  return {
    // City of Thirst's still is only in this list while the video it belongs to
    // is on the page — warming it before release would download artwork nothing
    // renders. See src/release.ts.
    "/watch": released
      ? [watchBakgrundNy, watchLogga, paradiseStill, cityOfThirst]
      : [watchBakgrundNy, watchLogga, paradiseStill],
    "/live": [liveBgSky, live],
    // Two skies on /contact, and they are two different pictures rather than one
    // file used twice the way /watch's are: `sky_bakgrund` has BOOKINGS, PR and
    // MGMT painted into it and sizes the canvas at desktop, `contact_background`
    // has no lettering at all and is the 426 × 980 portrait backdrop a phone
    // covers the screen with (ticket 026). Warming both is one more cache entry
    // in a bundle
    // the service worker precaches whole, not a request on the critical path.
    "/contact": [skyBakgrund, contactBackground, menuContact1Crop, signupPoster],
    // Just the background: it is what `.bg-wrapper` fades on, and Home's other
    // artwork is animated APNG that would cost far more held than it saves.
    "/": [nyBakgrund],
  };
}

export const ROUTE_ASSETS = buildRouteAssets(CITY_OF_THIRST_RELEASED);

export type Aspect = { width: number; height: number };

/** src → intrinsic size, populated only once `decode()` has actually resolved. */
const decoded = new Map<string, Aspect>();
/** In-flight warms, so a pointerdown cannot start a second decode of the same src. */
const inFlight = new Map<string, Promise<void>>();
/**
 * The prewarmed elements, kept referenced on purpose: an unreferenced image is
 * the cheapest thing for the browser to evict, and an evicted bitmap is exactly
 * what `isWarm` must not promise.
 */
const held: unknown[] = [];

/**
 * Whether this src is decoded and ready to paint. Safe during render, and only
 * ever true after a resolved `decode()` — claiming a bitmap we do not have would
 * show positioned artwork over a blank wrapper, which is worse than a fade.
 */
export function isWarm(src: string): boolean {
  return decoded.has(src);
}

export function aspectOf(src: string): Aspect | undefined {
  return decoded.get(src);
}

type WarmableImage = {
  src: string;
  fetchPriority?: string;
  naturalWidth?: number;
  naturalHeight?: number;
  width?: number;
  height?: number;
  decode(): Promise<unknown>;
};

type MakeImage = () => WarmableImage;

const defaultMake: MakeImage = () => new Image() as unknown as WarmableImage;

/**
 * Fetches and decodes one image. Resolves whether or not it worked: a failed
 * decode leaves the src cold, so the ordinary `onLoad` path handles it.
 */
export function warm(src: string, priority: "low" | "high" = "low", make = defaultMake) {
  if (!src || decoded.has(src)) return Promise.resolve();
  const existing = inFlight.get(src);
  if (existing) return existing;

  const run = (async () => {
    try {
      const img = make();
      img.fetchPriority = priority;
      img.src = src;
      held.push(img);
      await img.decode();
      const width = img.naturalWidth ?? img.width ?? 0;
      const height = img.naturalHeight ?? img.height ?? 0;
      // An aspect of 0 would give the wrapper a nonsense height, so it is not
      // worth recording — and without a record, the src stays cold.
      if (width > 0 && height > 0) decoded.set(src, { width, height });
    } catch {
      // Cold is a valid outcome.
    } finally {
      inFlight.delete(src);
    }
  })();

  inFlight.set(src, run);
  return run;
}

/** Warms everything one route needs. Called on intent, and by the idle sweep. */
export function warmRoute(route: string, priority: "low" | "high" = "high", make = defaultMake) {
  const assets = ROUTE_ASSETS[route];
  if (!assets) return Promise.resolve();
  return Promise.all(assets.map((src) => warm(src, priority, make))).then(() => undefined);
}

export type PrewarmDeps = IdleDeps & {
  connection?: Connection;
  warmOne?: (src: string, priority: "low" | "high") => Promise<void>;
  routes?: Readonly<Record<string, readonly string[]>>;
};

let started = false;

/**
 * Schedules the sweep: after load, then at idle, one image at a time at low
 * priority. Sequential and low on purpose — this must never be able to compete
 * with the landing page's own first paint, which is the thing the visitor is
 * actually looking at.
 */
export function prewarmRoutes(deps: PrewarmDeps = {}): void {
  if (started) return;
  started = true;

  const {
    connection = navigatorConnection(),
    warmOne = (src, priority) => warm(src, priority),
    routes = ROUTE_ASSETS,
    ...idle
  } = deps;

  if (!shouldPreload(connection)) return;

  const queue = Object.values(routes).flat();

  afterLoadThenIdle(async () => {
    for (const src of queue) await warmOne(src, "low");
  }, idle);
}

/** Test-only: the module-level guards would otherwise leak between cases. */
export function resetPrewarmForTest(): void {
  started = false;
  decoded.clear();
  inFlight.clear();
  held.length = 0;
}
